import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { ADMIN_PRIMARY, ADMIN_HAIRLINE } from '../../theme';
import { HELP_SECTION_NESTED_LABELS } from './helpArticles';

const NAV_GROUPS = [
  {
    title: 'Спортивный центр',
    items: [
      { id: 'centers', label: 'Спортивные центры' },
      {
        id: 'playgrounds',
        label: 'Площадки',
        children: [{ id: 'bookings', label: 'Бронирования' }],
      },
      {
        id: 'sections',
        label: 'Секции',
        children: HELP_SECTION_NESTED_LABELS.map((label) => ({
          id: `sec-${label}`,
          label,
        })),
      },
      { id: 'clients', label: 'Клиенты' },
      { id: 'staff', label: 'Сотрудники' },
    ],
  },
  {
    title: 'Прокат',
    items: [{ id: 'rental', label: 'Доп. услуги и прокат' }],
  },
];

function isActiveId(activeNav, id) {
  return activeNav === id;
}

function itemOrChildActive(activeNav, item) {
  if (isActiveId(activeNav, item.id)) return true;
  return (item.children || []).some((child) => isActiveId(activeNav, child.id) || child.label === activeNav);
}

function HelpScreenFrame({ classes, title, activeNav, caption, children }) {
  return (
    <figure className={classes.figure}>
      <div className={classes.chrome} aria-hidden="true">
        <span className={classes.dot} />
        <span className={classes.dot} />
        <span className={classes.dot} />
        <span className={classes.chromeTitle}>Панель администратора</span>
      </div>
      <div className={classes.stage}>
        <nav className={classes.sidebar} aria-hidden="true">
          <div className={classes.brand}>
            <div className={classes.brandName}>Площадка</div>
            <div className={classes.brandSub}>Панель администратора</div>
          </div>
          {NAV_GROUPS.map((group) => (
            <div key={group.title}>
              <div className={classes.navGroup}>{group.title}</div>
              {group.items.map((item) => {
                const open = itemOrChildActive(activeNav, item);
                const parentActive = isActiveId(activeNav, item.id);
                return (
                  <div key={item.id}>
                    <div className={parentActive ? classes.navActive : classes.navItem}>
                      {item.label}
                    </div>
                    {open && item.children
                      ? item.children.map((child) => (
                          <div
                            key={child.id}
                            className={
                              isActiveId(activeNav, child.id) || child.label === activeNav
                                ? classes.navNestedActive
                                : classes.navNested
                            }
                          >
                            {child.label}
                          </div>
                        ))
                      : null}
                  </div>
                );
              })}
            </div>
          ))}
        </nav>
        <div className={classes.content}>
          <div className={classes.pageTitle}>{title}</div>
          <div className={classes.panel}>{children}</div>
        </div>
      </div>
      {caption ? <figcaption className={classes.caption}>{caption}</figcaption> : null}
    </figure>
  );
}

HelpScreenFrame.propTypes = {
  classes: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  activeNav: PropTypes.string.isRequired,
  caption: PropTypes.string,
  children: PropTypes.node,
};

HelpScreenFrame.defaultProps = {
  caption: null,
  children: null,
};

const styles = (theme) => ({
  figure: {
    margin: 0,
  },
  chrome: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    height: 28,
    padding: '0 10px',
    backgroundColor: '#E2E8F0',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    border: `1px solid ${ADMIN_HAIRLINE}`,
    borderBottom: 'none',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#CBD5E1',
    display: 'inline-block',
  },
  chromeTitle: {
    marginLeft: 8,
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  stage: {
    display: 'flex',
    minHeight: 188,
    backgroundColor: '#f5f5f5',
    border: `1px solid ${ADMIN_HAIRLINE}`,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
  sidebar: {
    width: 148,
    flex: '0 0 148px',
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${ADMIN_HAIRLINE}`,
    padding: '8px 0 10px',
    fontSize: 10,
    lineHeight: 1.3,
    color: theme.palette.text.secondary,
  },
  brand: {
    padding: '4px 10px 8px',
  },
  brandName: {
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  brandSub: {
    fontSize: 9,
    color: theme.palette.text.secondary,
  },
  navGroup: {
    padding: '8px 10px 2px',
    fontSize: 9,
    fontWeight: 600,
    letterSpacing: '0.04em',
  },
  navItem: {
    padding: '3px 10px',
    color: theme.palette.text.primary,
  },
  navActive: {
    padding: '3px 10px 3px 6px',
    marginRight: 4,
    color: ADMIN_PRIMARY,
    fontWeight: 600,
    backgroundColor: 'rgba(14, 147, 95, 0.08)',
    boxShadow: `inset 4px 0 0 ${ADMIN_PRIMARY}`,
  },
  navNested: {
    padding: '2px 10px 2px 18px',
    color: theme.palette.text.secondary,
  },
  navNestedActive: {
    padding: '2px 10px 2px 14px',
    marginRight: 4,
    color: ADMIN_PRIMARY,
    fontWeight: 600,
    backgroundColor: 'rgba(14, 147, 95, 0.08)',
    boxShadow: `inset 4px 0 0 ${ADMIN_PRIMARY}`,
  },
  content: {
    flex: 1,
    minWidth: 0,
    padding: '10px 12px 12px',
  },
  pageTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: 8,
  },
  panel: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    border: `1px solid ${ADMIN_HAIRLINE}`,
    padding: 10,
    minHeight: 120,
  },
  caption: {
    marginTop: theme.spacing(1),
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
  },
});

const StyledFrame = withStyles(styles)(HelpScreenFrame);

function Chip({ label, tone }) {
  const bg = tone === 'ok' ? 'rgba(14, 147, 95, 0.12)' : '#EEF2F7';
  const color = tone === 'ok' ? ADMIN_PRIMARY : '#64748B';
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 10,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 8,
        backgroundColor: bg,
        color,
      }}
    >
      {label}
    </span>
  );
}

Chip.propTypes = {
  label: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(['ok', 'muted']),
};

Chip.defaultProps = {
  tone: 'muted',
};

const tableHead = {
  fontSize: 10,
  color: '#64748B',
  fontWeight: 600,
  padding: '4px 6px',
  borderBottom: `1px solid ${ADMIN_HAIRLINE}`,
  textAlign: 'left',
};

const tableCell = {
  fontSize: 11,
  color: '#1E293B',
  padding: '5px 6px',
};

export function StartIllustration() {
  return (
    <StyledFrame
      title="Спортивные центры"
      activeNav="centers"
      caption="Список центров: кнопка «Новый спортцентр» и колонка «Статус»."
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: '#fff',
            backgroundColor: ADMIN_PRIMARY,
            borderRadius: 6,
            padding: '4px 10px',
          }}
        >
          Новый спортцентр
        </span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={tableHead}>Город</th>
            <th style={tableHead}>Название</th>
            <th style={tableHead}>Адрес</th>
            <th style={tableHead}>Статус</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={tableCell}>—</td>
            <td style={tableCell}>—</td>
            <td style={tableCell}>—</td>
            <td style={tableCell}><Chip label="На проверке" /></td>
          </tr>
          <tr>
            <td style={tableCell}>—</td>
            <td style={tableCell}>—</td>
            <td style={tableCell}>—</td>
            <td style={tableCell}><Chip label="Одобрен" tone="ok" /></td>
          </tr>
        </tbody>
      </table>
    </StyledFrame>
  );
}

export function HoursIllustration() {
  return (
    <StyledFrame
      title="Редактирование площадки"
      activeNav="playgrounds"
      caption="Карточка площадки: блок «Режим работы», колонки «Будни» и «Выходные»."
    >
      <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4, color: '#1E293B' }}>Режим работы</div>
      <div style={{ fontSize: 10, color: '#64748B', marginBottom: 8 }}>
        Часы БЕЗ явного указания цены будут недоступны. Бесплатные часы - 0 рублей.
      </div>
      <div style={{ display: 'flex', gap: 8, fontSize: 10, marginBottom: 8 }}>
        <span style={{ fontWeight: 600, color: ADMIN_PRIMARY }}>Редактирование площадки</span>
        <span style={{ color: '#64748B' }}>Расписание</span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={tableHead}>Будни</th>
            <th style={tableHead}>Выходные</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={tableCell}>08:00–22:00 · 0</td>
            <td style={tableCell}>09:00–21:00 · 0</td>
          </tr>
        </tbody>
      </table>
    </StyledFrame>
  );
}

export function BookingsIllustration() {
  return (
    <StyledFrame
      title="Бронирования"
      activeNav="bookings"
      caption="Вложенный пункт «Бронирования» под «Площадки». Сетка — вкладка «Расписание»."
    >
      <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6, color: '#1E293B' }}>Расписание</div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={tableHead}>Время</th>
            <th style={tableHead}>Пн</th>
            <th style={tableHead}>Вт</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={tableCell}>10:00</td>
            <td style={{ ...tableCell, backgroundColor: 'rgba(14, 147, 95, 0.08)' }}>аренда</td>
            <td style={tableCell}> </td>
          </tr>
          <tr>
            <td style={tableCell}>11:00</td>
            <td style={tableCell}> </td>
            <td style={{ ...tableCell, backgroundColor: 'rgba(123, 31, 162, 0.08)' }}>секция</td>
          </tr>
        </tbody>
      </table>
    </StyledFrame>
  );
}

export function SectionsIllustration() {
  return (
    <StyledFrame
      title="Секции"
      activeNav="sections"
      caption="Ветка «Секции»: список программ и вложенные «Расписание», «Абонементы», «Заявки», «Персональные»."
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: '#fff',
            backgroundColor: ADMIN_PRIMARY,
            borderRadius: 6,
            padding: '4px 10px',
          }}
        >
          Добавить секцию
        </span>
      </div>
      <div
        style={{
          border: `1px solid ${ADMIN_HAIRLINE}`,
          borderRadius: 8,
          padding: 8,
          fontSize: 10,
          color: '#64748B',
        }}
      >
        <div style={{ fontWeight: 600, color: '#1E293B', marginBottom: 4 }}>
          Единое расписание площадки (зала)
        </div>
        Аренда, блокировки, занятия секций и персональные на одной шкале.
        <div style={{ marginTop: 6, color: ADMIN_PRIMARY, fontWeight: 600 }}>
          Настроить режим работы
        </div>
      </div>
    </StyledFrame>
  );
}

export function ClientsIllustration() {
  return (
    <StyledFrame
      title="Клиенты"
      activeNav="clients"
      caption="«Клиенты» — справочник организации. Абонемент и заявки — отдельные пункты под «Секции»."
    >
      <div style={{ fontSize: 10, color: '#64748B', marginBottom: 8 }}>
        Справочник клиентов организации: все клубы сети.
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={tableHead}>Клиент</th>
            <th style={tableHead}>Телефон</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={tableCell}>—</td>
            <td style={tableCell}>—</td>
          </tr>
        </tbody>
      </table>
      <div style={{ marginTop: 8, fontSize: 10, fontWeight: 600, color: ADMIN_PRIMARY }}>
        Выдать абонемент
      </div>
    </StyledFrame>
  );
}

export const HELP_ILLUSTRATIONS = {
  start: StartIllustration,
  hours: HoursIllustration,
  bookings: BookingsIllustration,
  sections: SectionsIllustration,
  clients: ClientsIllustration,
};
